/**
 * VoiceMode - ChatGPT-style voice interface for Lexy
 * 
 * Features:
 * - Full-screen voice mode overlay
 * - Animated pulsating orb while listening
 * - Speech-to-text using Web Speech API
 * - Text-to-speech for AI responses
 * - Visual feedback for different states
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, X, Volume2, Loader2, Phone } from 'lucide-react';
import { cn } from '../../lib/utils';

interface VoiceModeProps {
  isOpen: boolean;
  onClose: () => void;
  onSendMessage: (message: string) => Promise<string>;
  language?: 'nb' | 'en';
}

type VoiceState = 'idle' | 'listening' | 'processing' | 'speaking' | 'error';

// Check if Web Speech API is available
const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

const VoiceMode: React.FC<VoiceModeProps> = ({
  isOpen,
  onClose,
  onSendMessage,
  language = 'nb'
}) => {
  const [state, setState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolume] = useState(0);
  
  const recognitionRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  // Initialize speech recognition
  useEffect(() => {
    if (!SpeechRecognition) {
      setError('Nettleseren din støtter ikke talegjenkjenning');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language === 'nb' ? 'nb-NO' : 'en-US';

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      setTranscript(finalTranscript || interimTranscript);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        setError('Mikrofontilgang ble nektet. Vennligst gi tilgang og prøv igjen.');
      } else if (event.error !== 'aborted') {
        setError(`Feil ved talegjenkjenning: ${event.error}`);
      }
      setState('error');
    };

    recognition.onend = () => {
      if (state === 'listening') {
        // Auto-restart if still in listening mode
        try {
          recognition.start();
        } catch (e) {
          // Ignore if already started
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [language]);

  // Audio volume visualization
  const startAudioVisualization = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const updateVolume = () => {
        if (analyserRef.current && state === 'listening') {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          setVolume(average / 255);
          animationFrameRef.current = requestAnimationFrame(updateVolume);
        }
      };
      
      updateVolume();
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Kunne ikke få tilgang til mikrofonen');
    }
  };

  const stopAudioVisualization = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    setVolume(0);
  };

  // Start listening
  const startListening = async () => {
    setError(null);
    setTranscript('');
    setAiResponse('');
    
    try {
      await startAudioVisualization();
      recognitionRef.current?.start();
      setState('listening');
    } catch (err) {
      console.error('Error starting recognition:', err);
      setError('Kunne ikke starte talegjenkjenning');
      setState('error');
    }
  };

  // Stop listening and process
  const stopListening = async () => {
    recognitionRef.current?.stop();
    stopAudioVisualization();
    
    if (transcript.trim()) {
      setState('processing');
      
      try {
        const response = await onSendMessage(transcript);
        setAiResponse(response);
        setState('speaking');
        await speakResponse(response);
      } catch (err) {
        console.error('Error processing message:', err);
        setError('Kunne ikke behandle meldingen');
        setState('error');
      }
    } else {
      setState('idle');
    }
  };

  // Text-to-speech
  const speakResponse = async (text: string) => {
    // Use browser's built-in TTS for now (can be replaced with OpenAI TTS)
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === 'nb' ? 'nb-NO' : 'en-US';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    
    return new Promise<void>((resolve) => {
      utterance.onend = () => {
        setState('idle');
        resolve();
      };
      utterance.onerror = () => {
        setState('idle');
        resolve();
      };
      speechSynthesis.speak(utterance);
    });
  };

  // Handle open/close
  useEffect(() => {
    if (isOpen) {
      startListening();
    } else {
      stopAudioVisualization();
      recognitionRef.current?.stop();
      speechSynthesis.cancel();
      setState('idle');
      setTranscript('');
      setAiResponse('');
    }
  }, [isOpen]);

  // Cleanup
  useEffect(() => {
    return () => {
      stopAudioVisualization();
      speechSynthesis.cancel();
    };
  }, []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-gradient-to-b from-zinc-900 via-zinc-900 to-black flex flex-col items-center justify-center"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-3 rounded-full bg-zinc-800/50 hover:bg-zinc-700/50 transition-colors"
        >
          <X className="w-6 h-6 text-zinc-400" />
        </button>

        {/* End call button */}
        <button
          onClick={onClose}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 flex items-center gap-2 transition-colors"
        >
          <Phone className="w-5 h-5 rotate-[135deg]" />
          <span className="text-sm font-medium">Avslutt samtale</span>
        </button>

        {/* Main content */}
        <div className="flex flex-col items-center justify-center flex-1 px-8">
          {/* Pulsating orb */}
          <div className="relative mb-12">
            {/* Outer glow rings */}
            {state === 'listening' && (
              <>
                <motion.div
                  className="absolute inset-0 rounded-full bg-violet-500/20"
                  animate={{
                    scale: [1, 1.5 + volume * 0.5, 1],
                    opacity: [0.3, 0.1, 0.3],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    width: 200,
                    height: 200,
                    marginLeft: -100 + 60,
                    marginTop: -100 + 60,
                  }}
                />
                <motion.div
                  className="absolute inset-0 rounded-full bg-violet-500/10"
                  animate={{
                    scale: [1, 2 + volume * 0.8, 1],
                    opacity: [0.2, 0.05, 0.2],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.3,
                  }}
                  style={{
                    width: 200,
                    height: 200,
                    marginLeft: -100 + 60,
                    marginTop: -100 + 60,
                  }}
                />
              </>
            )}

            {/* Main orb */}
            <motion.div
              className={cn(
                "w-32 h-32 rounded-full flex items-center justify-center relative overflow-hidden",
                state === 'listening' && "bg-gradient-to-br from-violet-500 to-purple-600",
                state === 'processing' && "bg-gradient-to-br from-amber-500 to-orange-600",
                state === 'speaking' && "bg-gradient-to-br from-emerald-500 to-teal-600",
                state === 'idle' && "bg-gradient-to-br from-zinc-600 to-zinc-700",
                state === 'error' && "bg-gradient-to-br from-red-500 to-red-600"
              )}
              animate={state === 'listening' ? {
                scale: [1, 1.05 + volume * 0.1, 1],
              } : {}}
              transition={{
                duration: 0.2,
              }}
            >
              {/* Inner glow */}
              <div className="absolute inset-2 rounded-full bg-white/10 blur-sm" />
              
              {/* Icon */}
              {state === 'listening' && (
                <Mic className="w-12 h-12 text-white relative z-10" />
              )}
              {state === 'processing' && (
                <Loader2 className="w-12 h-12 text-white animate-spin relative z-10" />
              )}
              {state === 'speaking' && (
                <Volume2 className="w-12 h-12 text-white relative z-10" />
              )}
              {state === 'idle' && (
                <Mic className="w-12 h-12 text-white/70 relative z-10" />
              )}
              {state === 'error' && (
                <MicOff className="w-12 h-12 text-white relative z-10" />
              )}
            </motion.div>
          </div>

          {/* Status text */}
          <div className="text-center max-w-md">
            {state === 'listening' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-zinc-400 text-sm mb-4">Lytter...</p>
                {transcript && (
                  <p className="text-white text-lg font-medium">"{transcript}"</p>
                )}
                {!transcript && (
                  <p className="text-zinc-500 text-base">Si noe til Lexy...</p>
                )}
              </motion.div>
            )}

            {state === 'processing' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-amber-400 text-sm mb-2">Tenker...</p>
                <p className="text-zinc-400 text-base">"{transcript}"</p>
              </motion.div>
            )}

            {state === 'speaking' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-emerald-400 text-sm mb-4">Lexy svarer</p>
                <p className="text-white text-base leading-relaxed">{aiResponse}</p>
              </motion.div>
            )}

            {state === 'idle' && !error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-zinc-500 text-base">Trykk på mikrofonen for å snakke</p>
              </motion.div>
            )}

            {state === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-red-400 text-sm">{error}</p>
                <button
                  onClick={startListening}
                  className="mt-4 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-white text-sm transition-colors"
                >
                  Prøv igjen
                </button>
              </motion.div>
            )}
          </div>

          {/* Tap to stop hint */}
          {state === 'listening' && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              onClick={stopListening}
              className="mt-8 px-4 py-2 text-zinc-500 text-sm hover:text-zinc-400 transition-colors"
            >
              Trykk for å sende
            </motion.button>
          )}
        </div>

        {/* Lexy branding */}
        <div className="absolute top-6 left-6 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <span className="text-white text-sm">✦</span>
          </div>
          <span className="text-white font-semibold">Lexy</span>
          <span className="text-zinc-500 text-sm">Voice</span>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VoiceMode;
